import { EFinancingProgram, EPlanType, PlanPromoRule } from '@if/core/types'
import { normalizeDateTime } from '@if/core/utils'
import { Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import { FormatPromoCodeWithRates } from '@src/containers/App/utils/Formatter'
import { useGetProgramPlanOptionsByFinancingProgramId } from '@src/containers/BrowseMerchant/merchantHooks'
import SelectedPromoPlanDisplay from '@src/containers/EditMerchant/components/SelectedPromoPlanDisplay'
import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import { DATE_MAX } from '@src/data/types/Constants'
import { forEach } from 'lodash-es'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AsyncActionButton from '../../../components/AsyncActionButton'
import { MerchantPlanPromoRulesSchemaType } from './MerchantPromoPlanPanel'

interface Props {
  open: boolean
  handleClose: () => void
  availablePaymentPlans: ApplicableMerchantPaymentPlan[]
  control: Control<MerchantPlanPromoRulesSchemaType>
  supportFinancingPrograms?: EFinancingProgram[]
  setPromoCodeEntries: Dispatch<SetStateAction<PlanPromoRule[]>>
  entry: PlanPromoRule | undefined
}

const PlanPromoRuleDialog = ({
  open,
  handleClose,
  availablePaymentPlans,
  control,
  supportFinancingPrograms,
  setPromoCodeEntries,
  entry,
}: Props) => {
  const [selectedFinancingProgramId, setSelectedFinancingProgramId] = useState<EFinancingProgram>()
  const [selectedPlansName, setSelectedPlansName] = useState<EPlanType[]>([])
  const [selectedPromoCode, setSelectedPromoCode] = useState<ApplicableMerchantPaymentPlan>()
  const [selectablePromoCodes, setSelectablePromoCodes] = useState<string[]>([])

  const programPlanList = useGetProgramPlanOptionsByFinancingProgramId(selectedFinancingProgramId)

  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'planPromoRules',
  })

  const { t } = useTranslation()

  const { activePlans, expiredPlans, notActivatedPlans } = useMemo(() => {
    const today = normalizeDateTime(new Date())
    const active: ApplicableMerchantPaymentPlan[] = []
    const expired: ApplicableMerchantPaymentPlan[] = []
    const notActivated: ApplicableMerchantPaymentPlan[] = []

    availablePaymentPlans?.forEach((plan) => {
      const from = normalizeDateTime(plan.activeFrom)
      const to = plan.expiresOn ? normalizeDateTime(plan.expiresOn) : DATE_MAX
      const isExpired = today > to
      const isNotActivatedPlan = today < from

      if (isExpired) {
        expired.push(plan)
      } else if (isNotActivatedPlan) {
        notActivated.push(plan)
        active.push(plan)
      } else {
        active.push(plan)
      }
    })

    return {
      activePlans: active,
      expiredPlans: expired,
      notActivatedPlans: notActivated,
    }
  }, [availablePaymentPlans])

  const activePaymentPlansForRelated = activePlans

  const renderPlanGroup = useCallback(
    (plans: ApplicableMerchantPaymentPlan[], isDisabled: boolean) =>
      plans.map((plan) => {
        return (
          <MenuItem
            key={plan.id}
            value={plan.id}
            disabled={isDisabled}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            className={plan.creationPending ? 'yellow-outline-highlight' : ''}
          >
            <span>{FormatPromoCodeWithRates(plan)}</span>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: '8px' }}>
              {plan.expiresOn ? `(${new Date(plan.expiresOn).toLocaleDateString()})` : ''}
            </Typography>
          </MenuItem>
        )
      }),
    [],
  )

  const clearFields = () => {
    setSelectedFinancingProgramId(undefined)
    setSelectedPlansName([])
    setSelectedPromoCode(undefined)
    setSelectablePromoCodes([])
  }

  const addData = () => {
    const updatedEntries = [...value]

    forEach(selectedPlansName, (plan) => {
      const newEntry: PlanPromoRule = {
        defaultPromoCode: selectedPromoCode?.promoCode ?? '',
        planName: plan,
        financingProgramId: selectedFinancingProgramId as string,
        selectablePromoCodes: selectablePromoCodes,
      }

      const isExactDuplicate = updatedEntries.some(
        (entry) =>
          entry.defaultPromoCode === newEntry.defaultPromoCode &&
          entry.planName === newEntry.planName &&
          entry.financingProgramId === newEntry.financingProgramId &&
          entry.selectablePromoCodes.length === newEntry.selectablePromoCodes.length &&
          entry.selectablePromoCodes.every((code, idx) => code === newEntry.selectablePromoCodes[idx]),
      )

      if (isExactDuplicate) return

      const existingEntryIndex = updatedEntries.findIndex(
        (entry) => entry.planName === newEntry.planName && entry.financingProgramId === newEntry.financingProgramId,
      )

      if (existingEntryIndex !== -1) {
        updatedEntries[existingEntryIndex] = newEntry
      } else {
        updatedEntries.push(newEntry)
      }
    })

    setPromoCodeEntries(updatedEntries)
    onChange(updatedEntries)
  }

  const closeDialog = () => {
    handleClose()
    clearFields()
  }

  useEffect(() => {
    if (open && entry) {
      setSelectedFinancingProgramId(entry.financingProgramId as EFinancingProgram)
      setSelectedPlansName([entry.planName])
      const today = new Date()
      const matchingPlans = availablePaymentPlans.filter((p) => p.promoCode === entry.defaultPromoCode)

      let plan = matchingPlans.find((p) => {
        const expiryDate = p.expiresOn ? new Date(p.expiresOn) : new Date(DATE_MAX)
        return today <= expiryDate
      })

      if (!plan && matchingPlans.length > 0) {
        plan = matchingPlans[0]
      }

      setSelectedPromoCode(plan)
      setSelectablePromoCodes(Array.isArray(entry.selectablePromoCodes) ? entry.selectablePromoCodes : [])
    } else if (open) {
      clearFields()
    }
  }, [availablePaymentPlans, entry, open])

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
      <DialogTitle>{t(entry ? 'promoCodePanel.editPromo' : 'promoCodePanel.associatePromo')}</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ marginBottom: '2rem' }}>{t('promoCodePanel.provideAllFields')}</DialogContentText>
        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="financingProgramId">{t('promoCodePanel.programId')}</InputLabel>
              <Select
                labelId="financingProgramId"
                label={t('promoCodePanel.programId')}
                value={selectedFinancingProgramId}
                onChange={(e) => {
                  setSelectedPromoCode(undefined)
                  setSelectedPlansName([])
                  const newId = e.target.value as EFinancingProgram
                  setSelectedFinancingProgramId(newId)
                  setSelectablePromoCodes([])
                }}
                disabled={!!entry}
              >
                {supportFinancingPrograms?.map((id) => (
                  <MenuItem key={id} value={id}>
                    {t(`EFinancingProgram.${id}`, { ns: 'enum' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {selectedFinancingProgramId && (
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="planName">{t('promoCodePanel.plan')}</InputLabel>
                <Select
                  multiple
                  labelId="planName"
                  label={t('promoCodePanel.plan')}
                  value={selectedPlansName}
                  onChange={(e) => {
                    setSelectedPromoCode(undefined)
                    setSelectedPlansName(e.target.value as EPlanType[])
                    setSelectablePromoCodes([])
                  }}
                  disabled={!!entry}
                >
                  {programPlanList?.map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {t(`EPlanType.${plan as EPlanType}`, { ns: 'enum' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {selectedPlansName.length > 0 && selectedFinancingProgramId && (
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="defaultPromoCode">{t('promoCodePanel.defaultPromoCode')}</InputLabel>
                <Select
                  labelId="defaultPromoCode"
                  label={t('promoCodePanel.defaultPromoCode')}
                  value={selectedPromoCode ? selectedPromoCode.id : ''}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    const plan = availablePaymentPlans.find((p) => p.id === selectedId)

                    if (plan) {
                      setSelectablePromoCodes((prev) => {
                        const prevDefault = selectedPromoCode?.promoCode
                        let next = prev
                        if (prevDefault && prevDefault !== plan.promoCode) {
                          next = next.filter((code) => code !== prevDefault)
                        }
                        return next.filter((code) => code !== plan.promoCode)
                      })
                      setSelectedPromoCode(plan)
                    }
                  }}
                >
                  {[
                    ...renderPlanGroup(activePlans, false),
                    ...(notActivatedPlans.length > 0
                      ? [
                          <Divider key="not-activated-divider" sx={{ my: 1, position: 'relative' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {t('promoCodePanel.notActivated')}
                            </Typography>
                          </Divider>,
                          ...renderPlanGroup(notActivatedPlans, false),
                        ]
                      : []),
                    ...(expiredPlans.length > 0
                      ? [
                          <Divider key="expired-divider" sx={{ my: 1, position: 'relative' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {t('promoCodePanel.expired')}
                            </Typography>
                          </Divider>,
                          ...renderPlanGroup(expiredPlans, true),
                        ]
                      : []),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}
          {selectedPromoCode && (
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Typography variant="subtitle2">{t('promoCodePanel.selectablePromoCodes')}</Typography>
              <FormControl fullWidth>
                {activePaymentPlansForRelated.map((plan) => {
                  const label = FormatPromoCodeWithRates(plan)
                  const isDefault = selectedPromoCode?.promoCode === plan.promoCode
                  return (
                    <span key={plan.promoCode} style={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isDefault ? false : selectablePromoCodes.includes(plan.promoCode)}
                            disabled={isDefault}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectablePromoCodes((prev) => [...prev, plan.promoCode])
                              } else {
                                setSelectablePromoCodes((prev) => prev.filter((code) => code !== plan.promoCode))
                              }
                            }}
                          />
                        }
                        label={label}
                        sx={{ pr: 2 }}
                        className={plan.creationPending ? 'yellow-outline-highlight' : ''}
                      />
                    </span>
                  )
                })}
              </FormControl>
            </Grid>
          )}

          {selectedPromoCode && (
            <Grid container>
              <SelectedPromoPlanDisplay selectedPlan={selectedPromoCode} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <AsyncActionButton
          variant="contained"
          color="primary"
          onClick={() => {
            addData()
            closeDialog()
          }}
          disabled={!selectedPromoCode || !selectedFinancingProgramId || !selectedPlansName}
          fullWidth={false}
        >
          {t(entry ? 'promoCodePanel.editPromo' : 'promoCodePanel.addPromoCode')}
        </AsyncActionButton>
        <AsyncActionButton
          onClick={() => {
            closeDialog()
          }}
          fullWidth={false}
        >
          {t('common.cancel')}
        </AsyncActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default PlanPromoRuleDialog
